import { useCallback, useMemo, useState, type ReactNode } from "react";
import { FilterHeading } from "./ColumnFilterHeading";

/**
 * A column a table can be filtered on: `text` keeps the rows whose value contains what is typed, `set` keeps the rows whose value is
 * one of those ticked.
 */
export interface ColumnFilterSpec<T> {
  id: string;
  kind: "text" | "set";
  /** The value the filter tests, as shown in the cell. */
  value: (row: T) => string;
}

/** One column's filter: the text typed, or the values ticked. A column with no entry is not filtered. */
export type FilterValue = { kind: "text"; text: string } | { kind: "set"; selected: ReadonlySet<string> };

function passes<T>(row: T, spec: ColumnFilterSpec<T>, filter: FilterValue | undefined): boolean {
  if (filter === undefined) {
    return true;
  }
  const value = spec.value(row);
  return filter.kind === "text" ? value.toLocaleLowerCase().includes(filter.text.trim().toLocaleLowerCase()) : filter.selected.has(value);
}

/**
 * Filters a table's rows by its columns, and draws each filterable column's heading with a filter button. The library's own
 * filters (an expression builder) are not used: a text column is a single "contains" box, and a set column a checklist of its
 * values with a count beside each, both applied as they change. Each set column offers the values of every row, counted over the
 * rows the other filters leave.
 *
 * @param rows - Every row, before filtering.
 * @param specs - The filterable columns.
 * @returns The rows that pass every filter, and the heading for a column: its label with the filter button when it is filterable.
 */
export function useColumnFilters<T>(rows: readonly T[], specs: readonly ColumnFilterSpec<T>[]): { filtered: T[]; heading: (id: string, label: string) => ReactNode } {
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});

  const filtered = useMemo(() => rows.filter((row) => specs.every((spec) => passes(row, spec, filters[spec.id]))), [rows, specs, filters]);

  const setFilter = useCallback((id: string, filter: FilterValue | undefined): void =>
    setFilters((current) => {
      const next = { ...current };
      if (filter === undefined) {
        delete next[id];
      } else {
        next[id] = filter;
      }
      return next;
    }), []);

  // Stable until the rows or a filter change, so a table's memoised columns are rebuilt only then.
  const heading = useCallback((id: string, label: string): ReactNode => {
    const spec = specs.find((candidate) => candidate.id === id);
    if (!spec) {
      return label;
    }
    // The values a set column offers, counted over the rows the other columns' filters leave.
    const others = rows.filter((row) => specs.every((other) => other.id === id || passes(row, other, filters[other.id])));
    return <FilterHeading label={label} spec={spec} rows={rows} counted={others} filter={filters[id]} onChange={(filter) => setFilter(id, filter)} />;
  }, [rows, specs, filters, setFilter]);

  return { filtered, heading };
}
